import React, { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { timeProjectPlanningAPI } from '../services/api';
import {
  TimeProjectPlanningMember,
  TimeProjectPlanningOverview,
  TimeProjectPlanningProject,
} from '../types';
import TableLoadingRow from './TableLoadingRow';
import TimeSelect from './TimeSelect';

const emptyOverview: TimeProjectPlanningOverview = {
  members: [],
  projects: [],
};

const projectLabel = (project: TimeProjectPlanningProject) =>
  `${project.customerTitle} - ${project.projectTitle}`;

const ProjectPlanningPage: React.FC = () => {
  const [overview, setOverview] = useState<TimeProjectPlanningOverview>(emptyOverview);
  const [editingMember, setEditingMember] = useState<TimeProjectPlanningMember | null>(null);
  const [selectedProjectIds, setSelectedProjectIds] = useState<number[]>([]);
  const [showHiddenMembers, setShowHiddenMembers] = useState(false);
  const [visibilitySavingId, setVisibilitySavingId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadOverview = async (includeHidden = showHiddenMembers) => {
    setLoading(true);
    try {
      setOverview(await timeProjectPlanningAPI.getOverview(includeHidden));
    } catch {
      toast.error('Resource planning could not be loaded');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOverview(showHiddenMembers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showHiddenMembers]);

  const openEditor = (member: TimeProjectPlanningMember) => {
    setEditingMember(member);
    setSelectedProjectIds([...member.projectIds]);
  };

  const closeEditor = () => {
    setEditingMember(null);
    setSelectedProjectIds([]);
  };

  const handleSave = async (event: FormEvent) => {
    event.preventDefault();
    if (!editingMember || saving) {
      return;
    }

    setSaving(true);
    try {
      await timeProjectPlanningAPI.updateMemberProjects(editingMember.timeProfileId, selectedProjectIds);
      toast.success('Project links updated');
      closeEditor();
      await loadOverview(showHiddenMembers);
    } catch {
      toast.error('Project links could not be saved');
    } finally {
      setSaving(false);
    }
  };

  const handleVisibilityChange = async (
    member: TimeProjectPlanningMember,
    isHiddenFromProjectPlanning: boolean,
  ) => {
    if (visibilitySavingId !== null) {
      return;
    }

    setVisibilitySavingId(member.timeProfileId);
    try {
      await timeProjectPlanningAPI.updateMemberVisibility(
        member.timeProfileId,
        isHiddenFromProjectPlanning,
      );
      toast.success(isHiddenFromProjectPlanning
        ? 'Member hidden from resource planning'
        : 'Member shown in resource planning');
      await loadOverview(showHiddenMembers);
    } catch {
      toast.error('Member visibility could not be saved');
    } finally {
      setVisibilitySavingId(null);
    }
  };

  const projectsById = new Map(overview.projects.map(project => [project.projectId, project]));

  return (
    <div className="fade-in">
      <div className="page-header">
        <div>
          <h1>Resource planning</h1>
          <p>{overview.members.length} active members</p>
        </div>
        <label className="toggle-row planning-show-hidden-toggle">
          <input
            type="checkbox"
            checked={showHiddenMembers}
            onChange={(event) => setShowHiddenMembers(event.target.checked)}
          />
          Show hidden members
        </label>
      </div>

      {editingMember && (
        <div
          className="time-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !saving) {
              closeEditor();
            }
          }}
        >
          <section
            className="time-modal time-modal-wide"
            role="dialog"
            aria-modal="true"
            aria-labelledby="project-planning-dialog-title"
          >
            <div className="time-modal-header">
              <h5 id="project-planning-dialog-title" className="card-title">
                Edit projects for {editingMember.displayName}
              </h5>
              <button
                className="icon-button"
                type="button"
                onClick={closeEditor}
                aria-label="Close resource planning editor"
                disabled={saving}
              >
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            </div>
            <div className="time-modal-body">
              <form className="time-form" onSubmit={handleSave}>
                <div className="form-group">
                  <label className="form-label" htmlFor={`planning-projects-${editingMember.timeProfileId}`}>
                    Projects
                  </label>
                  <TimeSelect<number>
                    inputId={`planning-projects-${editingMember.timeProfileId}`}
                    ariaLabel={`Projects for ${editingMember.displayName}`}
                    isMulti
                    options={overview.projects.map(project => ({
                      value: project.projectId,
                      label: projectLabel(project),
                    }))}
                    value={selectedProjectIds}
                    onChange={(projectIds) => setSelectedProjectIds(projectIds)}
                    placeholder="Select projects"
                    noOptionsMessage="No projects available"
                    isDisabled={saving}
                  />
                  <p className="form-help-text">
                    Planning links do not limit time entries, reports, or project access.
                  </p>
                </div>
                <div className="form-actions">
                  <button
                    className="btn btn-secondary"
                    type="button"
                    onClick={() => setSelectedProjectIds([])}
                    disabled={saving || selectedProjectIds.length === 0}
                  >
                    Clear selection
                  </button>
                  <button className="btn btn-secondary" type="button" onClick={closeEditor} disabled={saving}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" type="submit" disabled={saving} aria-busy={saving}>
                    {saving && <span className="spinner-border spinner-border-sm" aria-hidden="true" />}
                    {saving ? 'Saving...' : 'Save projects'}
                  </button>
                </div>
              </form>
            </div>
          </section>
        </div>
      )}

      <section className="card">
        <div className="card-header">
          <h5 className="card-title">Member project links</h5>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover project-planning-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Linked projects</th>
                  <th>Visibility</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading && <TableLoadingRow colSpan={4} label="Loading resource planning..." />}
                {!loading && overview.members.map(member => {
                  const linkedProjects = member.projectIds
                    .map(projectId => projectsById.get(projectId))
                    .filter((project): project is TimeProjectPlanningProject => Boolean(project));

                  return (
                    <tr
                      key={member.timeProfileId}
                      className={member.isHiddenFromProjectPlanning ? 'planning-hidden-member-row' : undefined}
                    >
                      <td data-label="Member">
                        <div className="planning-member-heading">
                          <span className="planning-member-name">{member.displayName}</span>
                          {member.isHiddenFromProjectPlanning && (
                            <span className="badge badge-warning">Hidden</span>
                          )}
                        </div>
                        {member.email && <div className="planning-member-email">{member.email}</div>}
                      </td>
                      <td data-label="Linked projects">
                        {linkedProjects.length > 0 ? (
                          <div className="planning-project-list">
                            {linkedProjects.map(project => (
                              <span className="planning-project-chip" key={project.projectId}>
                                {projectLabel(project)}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="planning-empty-projects">No projects linked</span>
                        )}
                      </td>
                      <td data-label="Visibility">
                        <label className="toggle-row planning-visibility-toggle">
                          <input
                            type="checkbox"
                            checked={member.isHiddenFromProjectPlanning}
                            onChange={(event) => handleVisibilityChange(member, event.target.checked)}
                            disabled={visibilitySavingId !== null}
                            aria-label={`Hide ${member.displayName} from planning`}
                          />
                          Hide from planning
                        </label>
                      </td>
                      <td data-label="Actions">
                        <button
                          className="action-btn"
                          type="button"
                          onClick={() => openEditor(member)}
                          title="Edit projects"
                          aria-label={`Edit projects for ${member.displayName}`}
                        >
                          <i className="fa-solid fa-pen" aria-hidden="true" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {!loading && overview.members.length === 0 && (
                  <tr>
                    <td colSpan={4}><div className="empty-state">No active members</div></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectPlanningPage;
